import React, { useState } from 'react';
import { GeneratedComplaint, Tone } from './types';
import { UI } from './constants';
import { generateComplaint, postGenerate } from './services/geminiService';
import { generatePDF } from './services/pdfService';
import depositLaws from './data/security-deposit-laws.json';

type Kind = 'deposit' | 'complaint';
interface StateLaw { name: string; abbr: string; slug: string; citation: string; deadlineDays: number | null; businessDays?: boolean; deadline: string; cap: string; penalty: string; penaltyShort: string; notes?: string[]; }
const STATES: StateLaw[] = (depositLaws as any).states;

// Deadline date from the move-out date, counting business days where the state does (Arizona).
const deadlineFrom = (law: StateLaw, moveOut: string): Date | null => {
  if (!law.deadlineDays || !moveOut) return null;
  const d = new Date(moveOut + 'T12:00:00');
  if (isNaN(d.getTime())) return null;
  if (law.businessDays) {
    let n = 0;
    while (n < law.deadlineDays) { d.setDate(d.getDate() + 1); const w = d.getDay(); if (w !== 0 && w !== 6) n++; }
  } else d.setDate(d.getDate() + law.deadlineDays);
  return d;
};

const initialState = (() => {
  try { const st = new URLSearchParams(window.location.search).get('state'); return STATES.find(x => x.abbr === (st || '').toUpperCase())?.abbr || ''; } catch { return ''; }
})();

const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export default function App() {
  const [kind, setKind] = useState<Kind>(() => {
    try { return new URLSearchParams(window.location.search).get('type') === 'complaint' ? 'complaint' : 'deposit'; } catch { return 'deposit'; }
  });
  const [depState, setDepState] = useState(initialState);
  const [propertyAddress, setPropertyAddress] = useState('');
  const [moveOutDate, setMoveOutDate] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [amountReturned, setAmountReturned] = useState('');
  const [forwardingGiven, setForwardingGiven] = useState(false);
  const [forwardingAddress, setForwardingAddress] = useState('');
  const [senderName, setSenderName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [incidentDate, setIncidentDate] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [description, setDescription] = useState('');
  const [tone, setTone] = useState<Tone>('formal');
  const [compensationLevel, setCompensationLevel] = useState<number>(50);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const [generatedComplaint, setGeneratedComplaint] = useState<GeneratedComplaint | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);

  const t = UI;
  const inputClasses = "w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition bg-white text-slate-700 placeholder-slate-400";

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      const url = URL.createObjectURL(file);
      setImagePreview(url);
    }
  };

  const law = STATES.find(x => x.abbr === depState) || null;
  const dueDate = law ? deadlineFrom(law, moveOutDate) : null;
  const daysPast = dueDate ? Math.round((new Date().setHours(12, 0, 0, 0) - dueDate.getTime()) / 86400000) : null;

  const handleGenerate = async () => {
    if (kind === 'deposit') {
      if (!law) { setError('Please choose the state the rental is in.'); return; }
      if (!companyName.trim()) { setError("Please enter your landlord's or property manager's name."); return; }
      if (!depositAmount.trim()) { setError('Please enter how much deposit you paid.'); return; }
    } else {
      if (!companyName.trim()) {
        setError(t.companyNameError);
        return;
      }
      if (!description.trim()) {
        setError(t.descriptionError);
        return;
      }
    }
    
    setError(null);
    setIsGenerating(true);
    setGeneratedComplaint(null);

    try {
      let imageBase64: string | undefined = undefined;
      let imageMime: string | undefined = undefined;

      if (imageFile) {
        const fullBase64 = await readFileAsBase64(imageFile);
        imageBase64 = fullBase64.split(',')[1];
        imageMime = imageFile.type;
      }

      const complaint = kind === 'deposit'
        ? await postGenerate({
            kind: 'deposit', state: depState, senderName, company: companyName, propertyAddress, moveOutDate,
            depositAmount, amountReturned, forwardingGiven, forwardingAddress, description, tone, imageBase64, imageMimeType: imageMime,
          })
        : await generateComplaint(
        senderName,
        companyName,
        incidentDate,
        description,
        tone,
        compensationLevel,
        imageBase64,
        imageMime
      );

      setGeneratedComplaint(complaint);
    } catch (err: any) {
      console.error(err);
      setError(err.message || t.errorGeneric);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!generatedComplaint) return;
    let imageBase64: string | null = null;
    let imageMime: string | undefined = undefined;
    if (imageFile) {
        imageBase64 = await readFileAsBase64(imageFile);
        imageMime = imageFile.type;
    }
    generatePDF(generatedComplaint, companyName, imageBase64, imageMime);
  };

  const handleCopyText = () => {
    if (!generatedComplaint) return;
    const text = `Subject: ${generatedComplaint.subject}\n\n${generatedComplaint.body}`;
    navigator.clipboard.writeText(text);
    setShowCopySuccess(true);
    setTimeout(() => setShowCopySuccess(false), 3000);
  };

  const handleEmail = () => {
    if (!generatedComplaint) return;
    const subject = encodeURIComponent(generatedComplaint.subject);
    const body = encodeURIComponent(generatedComplaint.body);
    const cc = ccEmails.split(';').map(e => e.trim()).filter(e => e.length > 0).join(',');
    let mailto = recipientEmail ? `mailto:${recipientEmail}` : `mailto:`;
    const params = [];
    if (cc) params.push(`cc=${cc}`);
    params.push(`subject=${subject}`);
    params.push(`body=${body}`);
    mailto += `?${params.join('&')}`;
    window.location.href = mailto;
  };

  return (
    <div className="min-h-screen bg-brand-50 text-slate-800 p-4 md:p-8">
      {isInfoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in-up">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
            <button 
              onClick={() => setIsInfoOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h3 className="text-xl font-bold text-slate-800 mb-4">{t.infoTitle}</h3>
            <p className="text-slate-600 mb-6 leading-relaxed">
              {t.infoDesc}
            </p>
            
            <button 
              onClick={() => setIsInfoOpen(false)}
              className="w-full py-2 bg-slate-800 text-white rounded-lg font-semibold hover:bg-slate-900 transition"
            >
              {t.btnClose}
            </button>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto bg-white shadow-xl rounded-2xl overflow-hidden border border-brand-100">
        <header className="bg-brand-600 px-6 pt-6 text-white">
          <div className="flex justify-between items-start gap-4">
            <div>
              <p className="text-brand-100 text-xs font-semibold uppercase tracking-wider">{t.title}</p>
              <h1 className="text-2xl md:text-3xl font-bold mt-1 leading-tight">
                {kind === 'deposit' ? 'Get your security deposit back' : 'Turn a bad experience into a complaint that gets read'}
              </h1>
              <p className="text-brand-100 text-sm mt-2 max-w-xl">
                {kind === 'deposit'
                  ? "A demand letter built on your state's actual deposit law: the deadline, the statute and what the landlord risks by keeping your money."
                  : 'Describe what happened, pick a tone, and get a clear complaint letter ready to send.'}
              </p>
            </div>
            <button
              onClick={() => setIsInfoOpen(true)}
              className="text-brand-200 hover:text-white transition transform hover:scale-105 shrink-0"
              title="Info"
            >
              <InfoIcon />
            </button>
          </div>
          <div className="flex gap-1 mt-5" role="tablist">
            {([['deposit', 'Security deposit'], ['complaint', 'Other complaint']] as [Kind, string][]).map(([k, label]) => (
              <button
                key={k}
                role="tab"
                aria-selected={kind === k}
                onClick={() => { setKind(k); setError(null); setGeneratedComplaint(null); }}
                className={`px-4 py-2.5 rounded-t-lg text-sm font-semibold transition ${kind === k ? 'bg-white text-brand-600' : 'text-brand-100 hover:text-white hover:bg-white/10'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </header>

        <main className="p-6 md:p-8 space-y-8">
          <div className="space-y-6">
            {kind === 'deposit' ? (
            <div className="space-y-6">
              <div>
                <label htmlFor="dep-state" className="block text-sm font-semibold text-slate-700 mb-2">
                  State the rental is in <span className="text-red-500">*</span>
                </label>
                <select id="dep-state" className={inputClasses} value={depState} onChange={(e) => setDepState(e.target.value)}>
                  <option value="">Choose a state</option>
                  {STATES.map(x => <option key={x.abbr} value={x.abbr}>{x.name}</option>)}
                </select>
              </div>

              {law && (
                <div className="rounded-xl border border-brand-100 bg-brand-50 p-4 text-sm text-slate-700 space-y-2">
                  <p><span className="font-semibold text-slate-900">Deadline:</span> {law.deadline}</p>
                  <p><span className="font-semibold text-slate-900">If they don't comply:</span> {law.penalty}</p>
                  {dueDate && (
                    <p className={`font-semibold ${daysPast !== null && daysPast > 0 ? 'text-red-700' : 'text-slate-900'}`}>
                      Based on your move-out date, the deadline was {dueDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      {daysPast !== null && daysPast > 0 ? `, ${daysPast} day${daysPast === 1 ? '' : 's'} ago.` : daysPast === 0 ? ', which is today.' : '. It has not passed yet.'}
                    </p>
                  )}
                  <p className="text-slate-500">{law.citation} · <a className="text-brand-600 underline" href={`/security-deposit/${law.slug}`}>Full {law.name} rules</a></p>
                </div>
              )}

              <div>
                <label htmlFor="dep-landlord" className="block text-sm font-semibold text-slate-700 mb-2">
                  Landlord or property manager <span className="text-red-500">*</span>
                </label>
                <input id="dep-landlord" type="text" className={inputClasses} placeholder="e.g. Oakwood Property Management" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="dep-moveout" className="block text-sm font-semibold text-slate-700 mb-2">Move-out date</label>
                  <input id="dep-moveout" type="date" className={inputClasses} value={moveOutDate} onChange={(e) => setMoveOutDate(e.target.value)} />
                </div>
                <div>
                  <label htmlFor="dep-address" className="block text-sm font-semibold text-slate-700 mb-2">Rental address</label>
                  <input id="dep-address" type="text" className={inputClasses} placeholder="123 Main St, Apt 4" value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)} />
                </div>
                <div>
                  <label htmlFor="dep-amount" className="block text-sm font-semibold text-slate-700 mb-2">Deposit paid ($) <span className="text-red-500">*</span></label>
                  <input id="dep-amount" type="number" inputMode="decimal" min="0" className={inputClasses} placeholder="1800" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} />
                </div>
                <div>
                  <label htmlFor="dep-returned" className="block text-sm font-semibold text-slate-700 mb-2">Returned so far ($)</label>
                  <input id="dep-returned" type="number" inputMode="decimal" min="0" className={inputClasses} placeholder="0" value={amountReturned} onChange={(e) => setAmountReturned(e.target.value)} />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-brand-600" checked={forwardingGiven} onChange={(e) => setForwardingGiven(e.target.checked)} />
                  I gave the landlord my forwarding address in writing
                </label>
                {forwardingGiven && (
                  <input type="text" className={`${inputClasses} mt-3`} placeholder="Forwarding address (optional, it goes in the letter)" value={forwardingAddress} onChange={(e) => setForwardingAddress(e.target.value)} />
                )}
              </div>

              <div>
                <label htmlFor="dep-desc" className="block text-sm font-semibold text-slate-700 mb-2">What happened (optional)</label>
                <textarea
                  id="dep-desc"
                  className={`${inputClasses} h-28 resize-none`}
                  placeholder="E.g. They kept $650 for painting and carpet cleaning after 3 years. The carpet was 8 years old and I have move-out photos."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>
            ) : (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  {t.labelCompany} <span className="text-red-500">*</span>
                </label>
                <input 
                  type="text"
                  className={inputClasses}
                  placeholder={t.phCompany}
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </div>

               <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  {t.labelDescription} <span className="text-red-500">*</span>
                </label>
                <textarea 
                  className={`${inputClasses} h-32 resize-none`}
                  placeholder={t.phDescription}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>
            )}

             <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                {t.labelTone}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setTone('formal')}
                  className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                    tone === 'formal' ? 'bg-blue-50 border-brand-500 text-brand-700 shadow-sm ring-1 ring-brand-500' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  👮‍♂️ {t.toneFormal}
                </button>
                <button
                  type="button"
                  onClick={() => setTone('angry')}
                  className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                    tone === 'angry' ? 'bg-red-50 border-red-500 text-red-700 shadow-sm ring-1 ring-red-500' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  😡 {t.toneAngry}
                </button>
                <button
                  type="button"
                  onClick={() => setTone('legal')}
                  className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                    tone === 'legal' ? 'bg-slate-800 border-slate-900 text-white shadow-sm ring-1 ring-slate-900' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  ⚖️ {t.toneLegal}
                </button>
              </div>
            </div>

            {kind === 'complaint' && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-slate-700">
                  {t.labelCompensation}
                </label>
                <span className={`text-xs font-bold px-2 py-1 rounded ${
                  compensationLevel > 80 ? 'bg-red-100 text-red-700' : 'bg-brand-50 text-brand-700'
                }`}>
                  {Math.round(compensationLevel)}% Intensity
                </span>
              </div>
              <input 
                type="range"
                min="0"
                max="100"
                value={compensationLevel}
                onChange={(e) => setCompensationLevel(parseInt(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-600"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>{t.compReasonable}</span>
                <span className="font-bold text-red-500">{t.compGoBig}</span>
              </div>
            </div>
            )}

            <div className="border border-slate-200 rounded-lg overflow-hidden">
                <button 
                  onClick={() => setShowOptionalFields(!showOptionalFields)}
                  className="w-full px-4 py-3 bg-slate-50 flex justify-between items-center text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
                >
                   <span className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {showOptionalFields ? t.btnHideDetails : kind === 'deposit' ? 'Optional: your name, landlord email, photos' : t.btnShowDetails}
                   </span>
                   <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${showOptionalFields ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className={`transition-all duration-300 ease-in-out ${showOptionalFields ? 'max-h-[1000px] opacity-100 p-4 border-t border-slate-200' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                    <div className="space-y-6">
                         <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                            {t.labelSender}
                            </label>
                            <input type="text" className={inputClasses} placeholder={t.phSender} value={senderName} onChange={(e) => setSenderName(e.target.value)} />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {kind === 'complaint' && (
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                            {t.labelDate}
                            </label>
                            <input type="date" className={inputClasses} value={incidentDate} onChange={(e) => setIncidentDate(e.target.value)} />
                        </div>
                        )}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                            {t.labelRecipient}
                            </label>
                            <input type="email" className={inputClasses} placeholder={t.phRecipient} value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} />
                        </div>
                        </div>
                        <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            {t.labelCC}
                        </label>
                        <input type="text" className={inputClasses} placeholder={t.phCC} value={ccEmails} onChange={(e) => setCcEmails(e.target.value)} />
                        </div>
                        <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            {t.labelImage}
                        </label>
                        <div className="flex items-center space-x-4">
                            <label className="cursor-pointer bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-2 px-4 rounded-lg transition border border-slate-300 flex items-center shadow-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>Upload Image</span>
                            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                            </label>
                            {imagePreview && (
                            <div className="relative group">
                                <img src={imagePreview} alt="Preview" className="h-16 w-16 object-cover rounded-md border border-slate-300 shadow-sm" />
                                <button onClick={() => { setImageFile(null); setImagePreview(null); }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                </button>
                            </div>
                            )}
                        </div>
                        </div>
                    </div>
                </div>
            </div>

            {error && <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r"><p>{error}</p></div>}

            <button onClick={handleGenerate} disabled={isGenerating} className={`w-full py-4 rounded-xl font-bold text-lg text-white shadow-lg transition-all transform hover:-translate-y-1 ${isGenerating ? 'bg-slate-400 cursor-not-allowed' : 'bg-brand-600 hover:bg-brand-500 hover:shadow-brand-500/30'}`}>
              {isGenerating ? <span className="flex items-center justify-center"><svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>{t.btnGenerating}</span> : kind === 'deposit' ? 'Write my demand letter' : t.btnGenerate}
            </button>
          </div>

          {generatedComplaint && (
            <div className="animate-fade-in-up">
              <div className="border-t border-slate-200 my-8"></div>
              <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center"><span className="w-1.5 h-8 bg-brand-500 rounded-full mr-3"></span>{t.previewTitle}</h2>
              <div className="bg-white p-6 rounded-xl border border-slate-200 mb-6 text-sm leading-relaxed whitespace-pre-wrap text-slate-700 shadow-sm">
                <div className="mb-4 pb-4 border-b border-slate-100 flex items-start"><span className="font-bold text-slate-900 mr-2 min-w-[60px]">Subject:</span><span className="text-slate-800">{generatedComplaint.subject}</span></div>
                <div className="font-serif">{generatedComplaint.body}</div>
              </div>
              <div className="grid grid-cols-1 gap-4">
                 <button onClick={handleEmail} className="w-full flex items-center justify-center px-6 py-4 bg-brand-600 text-white rounded-xl font-bold text-lg hover:bg-brand-500 shadow-lg shadow-brand-500/30 transition transform hover:-translate-y-0.5"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>{t.btnEmail}</button>
                <div className="grid grid-cols-2 gap-4">
                  <button onClick={handleDownloadPDF} className="flex items-center justify-center px-4 py-3 bg-white border border-slate-200 rounded-lg font-semibold text-slate-700 hover:border-brand-500 hover:text-brand-600 hover:bg-brand-50 transition shadow-sm"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>{t.btnDownload}</button>
                  <button onClick={handleCopyText} className="flex items-center justify-center px-4 py-3 bg-white border border-slate-200 rounded-lg font-semibold text-slate-700 hover:border-brand-500 hover:text-brand-600 hover:bg-brand-50 transition shadow-sm">{showCopySuccess ? <span className="text-green-600 flex items-center animate-pulse"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>Copied!</span> : <><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>{t.btnCopy}</>}</button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
      <footer className="max-w-3xl mx-auto text-center text-sm text-slate-500 mt-6 flex flex-wrap justify-center gap-x-3 gap-y-1">
        <a className="hover:text-brand-600" href="/security-deposit/">Deposit laws by state</a>
        <span aria-hidden="true">·</span>
        <a className="hover:text-brand-600" href="/how-to-write-a-complaint-letter">Complaint letter guide</a>
        <span aria-hidden="true">·</span>
        <a className="hover:text-brand-600" href="/contact">Contact</a>
      </footer>
    </div>
  );
}